import { useState } from 'react';
import { cn } from '@rocco/utils/cn';

import type { B2BAssets, ProfileShowcaseSection } from '../types';

export interface B2BProfileShowcaseProps {
    section: ProfileShowcaseSection;
    assets: B2BAssets;
}

/**
 * "What your brand looks like on ArchiPro" (Figma `443:24358`, frame 1920×1384).
 *
 * Full-bleed dark photo with left-side editorial copy and a tall right-side
 * "SUPPLIER DETAIL" card rendered as a single exported image (Figma node
 * `443:24363`). All children positioned absolutely in the 1920 coordinate
 * system using % offsets and vw font sizing so the layout scales with viewport.
 *
 * **Interactive menu list:** the five rows ("Your Profile", "Your Products",
 * etc.) are hoverable. Hovering a row swaps the centre intro paragraph and
 * slides the chevron + active state to that row. All variants render in the
 * DOM with stacked opacity so the swap cross-fades smoothly. When no row is
 * hovered the menu falls back to the item with `active: true` (default
 * "Your Profile").
 *
 * Figma reference (1920-frame coords):
 *  - eyebrow `(Platform)`        x=25 y=439 w=118 h=29 — text 27px
 *  - headline                    x=25 y=415 w=885 h=258 — text 80px line 1.07 tracking -3.84
 *  - menu list                   x=25 y=869 w=314 h=194 — text 34px line 1.07 tracking -1.632
 *  - menu chevron                x=0 y=873 w=33 h=27
 *  - intro paragraph             x=339 y=865 w=577 h=176 — text 34px tracking -1.02
 *  - SUPPLIER DETAIL card image  x=1224 y=237 w=573 h=908 (image natively
 *    742×1077 — rotated brand name on the left rail overflows the card frame).
 */
export const B2BProfileShowcase = ({
    section,
    assets,
}: B2BProfileShowcaseProps) => {
    const background = assets[section.backgroundAssetKey];
    const cardImage = section.cardImageAssetKey
        ? assets[section.cardImageAssetKey]
        : undefined;

    const defaultActiveIndex = Math.max(
        section.menuItems.findIndex((it) => it.active),
        0,
    );
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    // Mobile uses click-to-select instead of hover (touch devices have no
    // hover). `clickedIndex` is the latched selection — falls back to the
    // default-active item until the user taps.
    const [clickedIndex, setClickedIndex] = useState<number | null>(null);
    const visibleIndex = clickedIndex ?? hoveredIndex ?? defaultActiveIndex;

    return (
        <section
            data-b2b-profile-showcase
            className="relative w-full overflow-hidden bg-[#1f1f1c] text-white max-md:aspect-auto md:aspect-[1920/1384]"
        >
            {/* Background photo + tint. On mobile the stacked content sits on
             *  top of the same photo; the overlay is darkened (60%) so the
             *  white title/menu/intro remain legible against the busy image. */}
            {background && (
                <img
                    src={background}
                    alt=""
                    className="pointer-events-none absolute inset-0 size-full object-cover"
                />
            )}
            <div
                className="absolute inset-0 bg-black/10 max-md:bg-black/60"
                aria-hidden="true"
            />

            {/* ----------------------------------------------------------
             * Desktop / tablet (≥md): absolute 1920-frame layout
             * ---------------------------------------------------------- */}
            <div className="absolute inset-0 max-md:hidden">
                {/* Eyebrow — top:439.45 / size:26.92px / tracking -1.29 */}
                <span
                    className="absolute font-sans text-[1.46vw] leading-[1.07] tracking-[-0.044em] text-white"
                    style={{
                        left: `${(25 / 1920) * 100}%`,
                        top: `${(439.45 / 1384) * 100}%`,
                    }}
                >
                    {section.eyebrow}
                </span>

                {/* Headline — top:415.28 / size:80px / line:1.07 / tracking -3.84 / first-line indent 193px / w:885 */}
                <h2
                    className="absolute whitespace-pre-wrap font-sans font-normal leading-[1.07] tracking-[-0.048em] text-white"
                    style={{
                        left: `${(25 / 1920) * 100}%`,
                        top: `${(415.28 / 1384) * 100}%`,
                        width: `${(885.45 / 1920) * 100}%`,
                        fontSize: `${80 / 19.2}vw`,
                        textIndent: `${(193 / 1920) * 100}vw`,
                    }}
                >
                    {section.title}
                </h2>

                {/* Menu list — x:25 y:869 w:314 h:194 */}
                <div
                    className="absolute"
                    style={{
                        left: `${(25 / 1920) * 100}%`,
                        top: `${(869 / 1384) * 100}%`,
                        width: `${(314 / 1920) * 100}%`,
                        height: `${(194 / 1384) * 100}%`,
                    }}
                    onMouseLeave={() => setHoveredIndex(null)}
                >
                    {/* Chevron — animates to the active row's y position. The
                        menu rows are 42 px apart (Figma stacks them at
                        y=0/42/84/126/168 inside the 194-tall list); the chevron
                        sits at y=4.14 relative to row 0. translateY uses vw
                        units so it scales with the 1920 design frame instead
                        of being relative to the SVG's own height. */}
                    <svg
                        className="absolute transition-transform duration-300 ease-out"
                        viewBox="0 0 33 27"
                        fill="none"
                        aria-hidden="true"
                        style={{
                            left: 0,
                            top: `${(4.14 / 194) * 100}%`,
                            width: `${(33.24 / 314) * 100}%`,
                            height: `${(26.73 / 194) * 100}%`,
                            transform: `translateY(${(visibleIndex * 42) / 19.2}vw)`,
                        }}
                    >
                        <path
                            d="M1 13.5 H30 M19 3 L30 13.5 L19 24"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="square"
                            strokeLinejoin="miter"
                            className="text-white"
                        />
                    </svg>
                    <ul
                        className="absolute flex flex-col font-sans leading-[1.07] tracking-[-0.048em]"
                        style={{
                            left: `${(53.09 / 314) * 100}%`,
                            top: `${(0.27 / 194) * 100}%`,
                            fontSize: `${34 / 19.2}vw`,
                            rowGap: `${6 / 19.2}vw`,
                        }}
                    >
                        {section.menuItems.map((item, index) => {
                            const isActive = index === visibleIndex;
                            return (
                                <li key={item.label}>
                                    <button
                                        type="button"
                                        onMouseEnter={() =>
                                            setHoveredIndex(index)
                                        }
                                        onFocus={() => setHoveredIndex(index)}
                                        onBlur={() => setHoveredIndex(null)}
                                        className={cn(
                                            'cursor-pointer whitespace-nowrap text-left transition-colors duration-200',
                                            isActive
                                                ? 'text-white'
                                                : 'text-[#cfcfcf] hover:text-white',
                                        )}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Intro paragraph — Figma x:424.5 y:865 w:577 h:176, 34px
                 * line 1.3 tracking -1.02. All variants are stacked at the
                 * same position; only the visible one is opacity-100 so the
                 * swap cross-fades. */}
                <div
                    className="absolute"
                    style={{
                        left: `${(424.52 / 1920) * 100}%`,
                        top: `${(865.27 / 1384) * 100}%`,
                        width: `${(577.22 / 1920) * 100}%`,
                        height: `${(176 / 1384) * 100}%`,
                    }}
                    aria-live="polite"
                >
                    {section.menuItems.map((item, index) => {
                        const text = item.intro ?? section.intro;
                        const isVisible = index === visibleIndex;
                        return (
                            <p
                                key={item.label}
                                className={cn(
                                    'absolute inset-0 font-sans leading-[1.3] tracking-[-0.03em] text-white transition-opacity duration-300 ease-out',
                                    isVisible
                                        ? 'opacity-100'
                                        : 'pointer-events-none opacity-0',
                                )}
                                style={{ fontSize: `${34 / 19.2}vw` }}
                            >
                                {text}
                            </p>
                        );
                    })}
                </div>

                {/* SUPPLIER DETAIL card — exported as a single image from
                 * Figma `443:24363`. Native 742×1077 (the rotated brand
                 * name on the left rail overflows the 573-wide card frame),
                 * so we position the image to keep its right edge aligned
                 * with the spec card's right edge (x≈1797). */}
                {cardImage && (
                    <img
                        src={cardImage}
                        alt=""
                        className="absolute drop-shadow-[0_0.24vw_4.39vw_rgba(0,0,0,0.1)]"
                        style={{
                            // 742 native width / 1920 frame = 38.65%
                            width: `${(742 / 1920) * 100}%`,
                            // Right edge anchored to x=1797 (spec card right)
                            left: `${((1797 - 742) / 1920) * 100}%`,
                            top: `${(237.69 / 1384) * 100}%`,
                            // height auto so natural aspect is preserved
                            height: 'auto',
                        }}
                    />
                )}
            </div>

            {/* ----------------------------------------------------------
             * Mobile (<md): vertical stack with Phase-2 vw tokens.
             * Eyebrow 3.255vw, title 6.5vw, menu 4.5vw, intro 3.5vw.
             * Content sits on top of the desktop bg photo (60% black tint
             * above) for atmosphere. Menu rows are real `<button>`s —
             * click swaps the active row and the intro paragraph below,
             * matching desktop hover behaviour for touch devices.
             * ---------------------------------------------------------- */}
            <div className="relative z-10 flex flex-col gap-[5vw] px-[5.4%] py-[8vw] md:hidden">
                <p className="font-sans text-[3.255vw] leading-[1.07] tracking-[-0.044em] text-white">
                    {section.eyebrow}
                </p>
                <h2 className="font-sans text-[6.5vw] leading-[1.07] tracking-[-0.048em] text-white">
                    {section.title}
                </h2>
                <ul className="flex flex-col font-sans text-[4.5vw] leading-[1.07] tracking-[-0.044em]">
                    {section.menuItems.map((item, index) => (
                        <li key={item.label}>
                            <button
                                type="button"
                                onClick={() => setClickedIndex(index)}
                                className={cn(
                                    'flex w-full items-center py-[1vw] text-left transition-colors',
                                    index === visibleIndex
                                        ? 'text-white'
                                        : 'text-[#cfcfcf]',
                                )}
                                aria-pressed={index === visibleIndex}
                            >
                                <span className="mr-[2vw] inline-block w-[4vw]">
                                    {index === visibleIndex ? '→' : ''}
                                </span>
                                {item.label}
                            </button>
                        </li>
                    ))}
                </ul>
                {/* All intros render stacked in one grid cell so the
                 *  container reserves space for the longest. Only the
                 *  active item is opaque — switching menus no longer
                 *  changes the section height (which previously
                 *  re-cropped the bg image). */}
                <div className="grid" aria-live="polite">
                    {section.menuItems.map((item, index) => {
                        const text = item.intro ?? section.intro;
                        const isVisible = index === visibleIndex;
                        return (
                            <p
                                key={item.label}
                                className={cn(
                                    'col-start-1 row-start-1 font-sans text-[3.5vw] leading-[1.35] tracking-[-0.030em] text-white/80 transition-opacity duration-200',
                                    isVisible
                                        ? 'opacity-100'
                                        : 'pointer-events-none opacity-0',
                                )}
                            >
                                {text}
                            </p>
                        );
                    })}
                </div>
                {cardImage && (
                    <img
                        src={cardImage}
                        alt=""
                        className="mt-[2vw] w-full rounded-[1vw]"
                    />
                )}
            </div>
        </section>
    );
};
